import { useEffect, useState } from "react";
import { FaMedal, FaStar } from "react-icons/fa";
import { Avatar, Message, Spin } from "@/frontend/components/common";
import { bettingPoolService } from "@/frontend/services";
import type { PoolLeaderboardEntryDTO } from "@/shared/dto";

interface PoolLeaderboardTabProps {
  poolId: string;
}

const MEDAL_COLORS: Record<number, string> = {
  1: "text-yellow-500",
  2: "text-gray-400",
  3: "text-amber-600",
};

const RANK_BACKGROUNDS: Record<number, string> = {
  1: "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200",
  2: "bg-gradient-to-r from-gray-50 to-slate-100 border-gray-200",
  3: "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200",
};

export function PoolLeaderboardTab({ poolId }: PoolLeaderboardTabProps) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<PoolLeaderboardEntryDTO[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const response = await bettingPoolService.getLeaderboard(poolId);
        if (!active) return;
        if (response.success && response.data) {
          setEntries(response.data);
        } else {
          Message.error({ content: response.error || "Error al cargar la tabla de posiciones" });
        }
      } catch (error) {
        if (!active) return;
        Message.error({
          content: error instanceof Error ? error.message : "Error al cargar la tabla de posiciones",
        });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [poolId]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  if (entries.length === 0) {
    return <p className="text-gray-500 text-center py-8">Aún no hay puntuaciones registradas.</p>;
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <div
          key={entry.participantId}
          className={`flex items-center justify-between p-3 rounded-lg border ${
            RANK_BACKGROUNDS[entry.rank] || "bg-white border-gray-100"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 flex justify-center">
              {entry.rank <= 3 ? (
                <FaMedal className={`text-xl ${MEDAL_COLORS[entry.rank]}`} />
              ) : (
                <span className="font-semibold text-gray-500">{entry.rank}</span>
              )}
            </div>
            <Avatar name={entry.userName} src={entry.userAvatar || undefined} />
            <span className="font-medium text-gray-800">{entry.userName}</span>
          </div>
          <div className="flex items-center gap-1 font-semibold text-gray-700">
            <FaStar className="text-yellow-500" />
            {entry.totalPoints} pts
          </div>
        </div>
      ))}
    </div>
  );
}
